import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { applySmartTheming, getSmartThemeRecommendations } from '../utils/smartTheming';
import { useAuth } from './AuthContext';
// TODO: Remove direct Firestore imports - use backend API instead
// import { doc, updateDoc, setDoc } from 'firebase/firestore';
// import { db, localDb } from '../firebase/config';
import premiumTheme from '../theme/premiumTheme';
import { toast } from 'react-toastify';

const ThemeContext = createContext({});

export const useCustomTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useCustomTheme must be used within a ThemeContextProvider');
  }
  return context;
};

// Custom theme configurations
const customThemes = {
  default: {
    palette: {
      mode: 'light',
      primary: {
        main: '#FF8C42',
        light: '#FFB74D',
        dark: '#F57C00',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#E85A4F',
        light: '#FF6B35',
        dark: '#D32F2F',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#fef7ed',
        paper: '#ffffff',
      },
      text: {
        primary: '#1a1a1a',
        secondary: '#404040',
      },
    },
  },

  sober: {
    palette: {
      mode: 'light',
      primary: {
        main: '#64748b',
        light: '#94a3b8',
        dark: '#475569',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#6b7280',
        light: '#9ca3af',
        dark: '#4b5563',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#f1f5f9',
        paper: '#ffffff',
      },
      text: {
        primary: '#0f172a',
        secondary: '#475569',
      },
    },
    typography: {
      fontFamily: "'Inter', 'Roboto', sans-serif",
      h1: { fontWeight: 600, color: '#1e293b' },
      h2: { fontWeight: 600, color: '#334155' },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            background: '#ffffff',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
    },
  },

  dark: {
    palette: {
      mode: 'dark',
      primary: {
        main: '#3b82f6',
        light: '#60a5fa',
        dark: '#2563eb',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#8b5cf6',
        light: '#a78bfa',
        dark: '#7c3aed',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#030712',
        paper: '#0a0f1a',
      },
      text: {
        primary: '#f1f5f9',
        secondary: '#cbd5e1',
      },
      divider: '#1e293b',
    },
    typography: {
      fontFamily: "'Inter', 'Roboto', sans-serif",
      h1: { fontWeight: 600, color: '#f1f5f9' },
      h2: { fontWeight: 600, color: '#e2e8f0' },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            background: '#0a0f1a',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.4)',
            border: '1px solid #1e293b',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: '#0a0f1a',
            borderBottom: '1px solid #1e293b',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: '#0a0f1a',
            borderColor: '#1e293b',
          },
        },
      },
    },
  },

  'dark-maximalist': {
    palette: {
      mode: 'dark',
      primary: {
        main: '#06b6d4',
        light: '#22d3ee',
        dark: '#0891b2',
        contrastText: '#020617',
      },
      secondary: {
        main: '#8b5cf6',
        light: '#a78bfa',
        dark: '#7c3aed',
        contrastText: '#ffffff',
      },
      success: {
        main: '#10b981',
        light: '#34d399',
        dark: '#059669',
        contrastText: '#020617',
      },
      warning: {
        main: '#f59e0b',
        light: '#fbbf24',
        dark: '#d97706',
        contrastText: '#020617',
      },
      error: {
        main: '#f43f5e',
        light: '#fb7185',
        dark: '#e11d48',
        contrastText: '#020617',
      },
      info: {
        main: '#0ea5e9',
        light: '#38bdf8',
        dark: '#0284c7',
        contrastText: '#020617',
      },
      background: {
        default: '#000000',
        paper: '#000000',
      },
      text: {
        primary: '#f8fafc',
        secondary: '#cbd5e1',
      },
      divider: '#1a1a1a',
    },
    typography: {
      fontFamily: "'Outfit', 'Inter', 'Roboto', sans-serif",
      h1: { fontWeight: 800, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontWeight: 700, letterSpacing: '-0.01em' },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    customEffects: {
      gradients: {
        primary: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)',
        secondary: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
        accent: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
        luxury: 'linear-gradient(135deg, #0ea5e9 0%, #8b5cf6 100%)',
        luxuryText: 'linear-gradient(135deg, #f8fafc 0%, #94a3b8 100%)',
        glass: 'linear-gradient(145deg, rgba(0, 0, 0, 0.85) 0%, rgba(10, 10, 10, 0.65) 100%)',
        nav: 'linear-gradient(135deg, #000000 0%, #0a0a0a 100%)',
      },
      animations: {
        float: 'float 6s ease-in-out infinite',
        shimmer: 'shimmer 1.5s infinite',
        glow: 'glow 2s ease-in-out infinite alternate',
      },
      glass: {
        background: 'rgba(0, 0, 0, 0.55)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        backdropFilter: 'blur(18px)',
      },
      shadows: {
        glow: '0 0 24px rgba(6, 182, 212, 0.35)',
        glowLuxury: '0 0 40px rgba(139, 92, 246, 0.35)',
        premium: '0 25px 60px rgba(2, 6, 23, 0.6), 0 0 80px rgba(6, 182, 212, 0.18)',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: '#000000',
            backgroundImage: 'none',
            backgroundAttachment: 'fixed',
          },
          '::selection': {
            backgroundColor: 'rgba(6, 182, 212, 0.25)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: '#000000',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            background: '#000000',
            border: '1px solid #111111',
            boxShadow: '0 10px 30px rgba(2, 6, 23, 0.6)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: '#0f172a',
            borderColor: '#1e293b',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: '#0f172a',
            borderBottom: '1px solid #1e293b',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
          },
          containedPrimary: {
            background: 'linear-gradient(90deg, #06b6d4, #22d3ee)',
            color: '#020617',
            boxShadow: '0 0 20px rgba(6, 182, 212, 0.3)',
          },
          containedSecondary: {
            background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)',
            color: '#020617',
            boxShadow: '0 0 20px rgba(139, 92, 246, 0.3)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 600,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: 12,
            },
          },
        },
      },
    },
  },
};

// Industry-specific theme configurations  
const industryThemes = {
  medical: {
    palette: {
      mode: 'dark',
      primary: {
        main: '#10b981',
        light: '#34d399',
        dark: '#059669',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#60a5fa',
        light: '#93c5fd',
        dark: '#3b82f6',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#0a1a14',
        paper: '#0f2318',
      },
      text: {
        primary: '#f0fdf4',
        secondary: '#a7f3d0',
      },
      success: {
        main: '#10b981',
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: '1px solid rgba(16, 185, 129, 0.2)',
            boxShadow: '0 4px 16px rgba(16, 185, 129, 0.15)',
          },
        },
      },
    },
  },

  beauty: {
    palette: {
      mode: 'dark',
      primary: {
        main: '#ec4899',
        light: '#f472b6',
        dark: '#be185d',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#fbbf24',
        light: '#fcd34d',
        dark: '#f59e0b',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#1a0a14',
        paper: '#2a1020',
      },
      text: {
        primary: '#fdf2f8',
        secondary: '#f9a8d4',
      },
    },
    typography: {
      h1: {
        fontFamily: "'Playfair Display', serif",
        fontWeight: 700,
      },
      h2: {
        fontFamily: "'Playfair Display', serif",
        fontWeight: 600,
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            background: 'linear-gradient(135deg, rgba(42,16,32,0.95) 0%, rgba(26,10,20,0.95) 100%)',
            boxShadow: '0 8px 32px rgba(236, 72, 153, 0.2)',
          },
        },
      },
    },
  },

  fitness: {
    palette: {
      mode: 'dark',
      primary: {
        main: '#00acc1',
        light: '#26c6da',
        dark: '#0097a7',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#ffab00',
        light: '#ffcc02',
        dark: '#ff8f00',
        contrastText: '#ffffff',
      },
      background: {
        default: '#1a1a1a',
        paper: 'rgba(66, 66, 66, 0.95)',
      },
      text: {
        primary: '#ffffff',
        secondary: '#e0e0e0',
      },
    },
    typography: {
      h1: {
        fontFamily: "'Oswald', sans-serif",
        fontWeight: 700,
        textTransform: 'uppercase',
      },
      h2: {
        fontFamily: "'Oswald', sans-serif",
        fontWeight: 600,
        textTransform: 'uppercase',
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            background: 'linear-gradient(135deg, rgba(66,66,66,0.95) 0%, rgba(33,33,33,0.95) 100%)',
            border: '2px solid rgba(0, 172, 193, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 172, 193, 0.25)',
          },
        },
      },
    },
  },

  restaurant: {
    palette: {
      mode: 'dark',
      primary: {
        main: '#ef5350',
        light: '#ff867c',
        dark: '#d32f2f',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#ff9800',
        light: '#ffb74d',
        dark: '#f57c00',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#1a0f0a',
        paper: '#2a1a12',
      },
      text: {
        primary: '#fff8e1',
        secondary: '#ffcc80',
      },
    },
    typography: {
      h1: {
        fontFamily: "'Merriweather', serif",
        fontWeight: 700,
      },
      h2: {
        fontFamily: "'Merriweather', serif",
        fontWeight: 600,
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            background: 'linear-gradient(135deg, rgba(42,26,18,0.95) 0%, rgba(26,15,10,0.95) 100%)',
            boxShadow: '0 8px 32px rgba(211, 47, 47, 0.2)',
            border: '1px solid rgba(255, 193, 7, 0.15)',
          },
        },
      },
    },
  },

  automotive: {
    palette: {
      mode: 'dark',
      primary: {
        main: '#ef5350',
        light: '#ff867c',
        dark: '#d32f2f',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#757575',
        light: '#9e9e9e',
        dark: '#424242',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
      text: {
        primary: '#f5f5f5',
        secondary: '#bdbdbd',
      },
    },
    typography: {
      h1: {
        fontFamily: "'Anton', sans-serif",
        fontWeight: 400,
        textTransform: 'uppercase',
      },
      h2: {
        fontFamily: "'Anton', sans-serif",
        fontWeight: 400,
        textTransform: 'uppercase',
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            background: 'linear-gradient(135deg, rgba(30,30,30,0.95) 0%, rgba(18,18,18,0.95) 100%)',
            boxShadow: '0 8px 32px rgba(33, 33, 33, 0.5)',
            border: '2px solid rgba(211, 47, 47, 0.2)',
          },
        },
      },
    },
  },

  professional: {
    palette: {
      mode: 'dark',
      primary: {
        main: '#3b82f6',
        light: '#60a5fa',
        dark: '#1e3a8a',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#10b981',
        light: '#34d399',
        dark: '#059669',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#0c1222',
        paper: '#131d33',
      },
      text: {
        primary: '#f1f5f9',
        secondary: '#94a3b8',
      },
    },
    typography: {
      fontFamily: "'Inter', 'Roboto', sans-serif",
      h1: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            background: 'rgba(19,29,51,0.95)',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.15)',
            border: '1px solid rgba(59, 130, 246, 0.15)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
    },
  },

  creative: {
    palette: {
      mode: 'dark',
      primary: {
        main: '#a78bfa',
        light: '#c4b5fd',
        dark: '#8b5cf6',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#ec4899',
        light: '#f472b6',
        dark: '#db2777',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#0f0a1a',
        paper: '#1a1028',
      },
      text: {
        primary: '#f5f3ff',
        secondary: '#c4b5fd',
      },
    },
    typography: {
      h1: {
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 800,
        background: 'linear-gradient(135deg, #a78bfa 0%, #f472b6 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      },
      h2: {
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 700,
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            background: 'linear-gradient(135deg, rgba(26,16,40,0.95) 0%, rgba(15,10,26,0.95) 100%)',
            boxShadow: '0 20px 40px rgba(139, 92, 246, 0.2)',
            backdropFilter: 'blur(20px)',
          },
        },
      },
    },
  },
};

// Color contrast utility functions
const calculateLuminance = (rgb) => {
  const [r, g, b] = rgb.map((val) => {
    val /= 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

const getContrastRatio = (color1, color2) => {
  const lum1 = calculateLuminance(hexToRgb(color1));
  const lum2 = calculateLuminance(hexToRgb(color2));
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
};

// Enhanced theme validation and contrast fixes
const validateAndFixTheme = (theme) => {
  const fixedTheme = JSON.parse(JSON.stringify(theme)); // Deep clone
  
  // Define minimum contrast ratios (WCAG AA standards)
  const MIN_CONTRAST_NORMAL = 4.5;
  const MIN_CONTRAST_LARGE = 3;
  
  // Safe fallback colors with good contrast
  const safeFallbacks = {
    light: {
      text: { primary: '#1a1a1a', secondary: '#404040' },
      background: { default: '#ffffff', paper: '#ffffff' }
    },
    dark: {
      text: { primary: '#ffffff', secondary: '#e0e0e0' },
      background: { default: '#030712', paper: '#0a0f1a' }
    }
  };
  
  // Check if background is gradient and provide solid fallback
  if (fixedTheme.palette?.background?.default?.includes('gradient')) {
    if (fixedTheme.palette.mode === 'dark') {
      fixedTheme.palette.background.default = '#030712';
    } else {
      fixedTheme.palette.background.default = '#ffffff';
    }
  }

  // Ensure paper background is solid and has good contrast
  if (fixedTheme.palette?.background?.paper?.includes('rgba') ||
      fixedTheme.palette?.background?.paper?.includes('gradient')) {
    if (fixedTheme.palette.mode === 'dark') {
      fixedTheme.palette.background.paper = '#0a0f1a';
    } else {
      fixedTheme.palette.background.paper = '#ffffff';
    }
  }
  
  // Fix text colors for better contrast
  const bgColor = fixedTheme.palette?.background?.default?.replace(/gradient.*/, '#ffffff') || '#ffffff';
  const isDark = fixedTheme.palette?.mode === 'dark';
  
  if (isDark) {
    fixedTheme.palette.text = {
      primary: '#ffffff',
      secondary: '#e0e0e0'
    };
  } else {
    fixedTheme.palette.text = {
      primary: '#1a1a1a',
      secondary: '#404040'
    };
  }
  
  // Fix typography colors that might be problematic
  if (fixedTheme.typography?.h1?.color) {
    // Remove gradient text effects that might not work everywhere
    if (fixedTheme.typography.h1.background?.includes('gradient')) {
      delete fixedTheme.typography.h1.background;
      delete fixedTheme.typography.h1.WebkitBackgroundClip;
      delete fixedTheme.typography.h1.WebkitTextFillColor;
      fixedTheme.typography.h1.color = isDark ? '#ffffff' : '#1a1a1a';
    }
  }
  
  if (fixedTheme.typography?.h2?.color) {
    if (fixedTheme.typography.h2.background?.includes('gradient')) {
      delete fixedTheme.typography.h2.background;
      delete fixedTheme.typography.h2.WebkitBackgroundClip;
      delete fixedTheme.typography.h2.WebkitTextFillColor;
      fixedTheme.typography.h2.color = isDark ? '#e0e0e0' : '#2a2a2a';
    }
  }
  
  // Ensure component backgrounds are solid and accessible
  if (fixedTheme.components?.MuiCard?.styleOverrides?.root?.background?.includes('gradient')) {
    if (isDark) {
      fixedTheme.components.MuiCard.styleOverrides.root.background = '#1e1e1e';
      fixedTheme.components.MuiCard.styleOverrides.root.color = '#ffffff';
    } else {
      fixedTheme.components.MuiCard.styleOverrides.root.background = '#ffffff';
      fixedTheme.components.MuiCard.styleOverrides.root.color = '#1a1a1a';
    }
  }
  
  return fixedTheme;
};

// Apply fixes to all themes
const fixedCustomThemes = Object.keys(customThemes).reduce((acc, key) => {
  acc[key] = validateAndFixTheme(customThemes[key]);
  return acc;
}, {});

const fixedIndustryThemes = Object.keys(industryThemes).reduce((acc, key) => {
  acc[key] = validateAndFixTheme(industryThemes[key]);
  return acc;
}, {});

// Combine all themes with fixes applied
const allThemes = { ...fixedCustomThemes, ...fixedIndustryThemes };

export const ThemeContextProvider = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const [currentThemeKey, setCurrentThemeKey] = useState('dark-maximalist');
  const [businessContext, setBusinessContext] = useState({});
  const [customizations, setCustomizations] = useState({});

  // Load theme from user profile when available
  useEffect(() => {
    if (userProfile?.themePreference) {
      setCurrentThemeKey(userProfile.themePreference);
    }
  }, [userProfile]);

  // Save theme to user profile via backend API
  const saveThemeToProfile = async (themeKey) => {
    if (currentUser) {
      // ✅ PATTERN A COMPLIANT: Save via backend API
      try {
        const API_URL = process.env.REACT_APP_SECURE_API_URL ||
          'https://us-central1-booker-1d469.cloudfunctions.net/secureApi';

        const token = await currentUser.getIdToken();

        await fetch(`${API_URL}/user/theme`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            themePreference: themeKey
          })
        });

        // Get theme display name
        const themeDisplayName = themeKey.charAt(0).toUpperCase() + themeKey.slice(1);

        toast.success(`Theme changed to ${themeDisplayName}`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        console.log('Theme preference saved:', themeKey);
        return;
      } catch (error) {
        console.error('Error saving theme preference:', error);
      }

      // Fallback to localStorage if API fails

      /* COMMENTED OUT - Direct Firestore access
      try {
        const userDoc = doc(db, 'users', currentUser.uid);
        await updateDoc(userDoc, {
          themePreference: themeKey,
          updatedAt: new Date()
        });

        // Get theme display name
        const themeDisplayName = themeKey.charAt(0).toUpperCase() + themeKey.slice(1);

        toast.success(`Theme changed to ${themeDisplayName}`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        console.log('Theme preference saved:', themeKey);
      } catch (error) {
        console.error('Error saving theme preference:', error);
        // If user document doesn't exist, create it with the theme preference
        try {
          const newUserDoc = doc(db, 'users', currentUser.uid);
          await setDoc(newUserDoc, {
            themePreference: themeKey,
            email: currentUser.email,
            displayName: currentUser.displayName,
            createdAt: new Date(),
            updatedAt: new Date()
          }, { merge: true });

          // Get theme display name
          const themeDisplayName = themeKey.charAt(0).toUpperCase() + themeKey.slice(1);

          toast.success(`Theme changed to ${themeDisplayName}`, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });

          console.log('Theme preference saved to new user document:', themeKey);
        } catch (createError) {
          console.error('Error creating user document with theme:', createError);
          toast.error('Failed to save theme preference. Please try again.', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      }
      */

      // Temporary: Store theme in localStorage until API is implemented
      try {
        localStorage.setItem('themePreference', themeKey);
        const themeDisplayName = themeKey.charAt(0).toUpperCase() + themeKey.slice(1);

        toast.success(`Theme changed to ${themeDisplayName}`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        console.log('Theme preference saved to localStorage (temporary):', themeKey);
      } catch (error) {
        console.error('Error saving theme preference to localStorage:', error);
        toast.error('Failed to save theme preference. Please try again.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } else {
      toast.warn('Please sign in to save theme preferences', {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Helper function to safely create gradient with fallback
  const createSafeGradient = (color1, color2) => {
    if (color1 && color2) {
      return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
    }
    return color1 || '#1976d2'; // Fallback to MUI default blue
  };

  // Generate the actual Material-UI theme
  const generateTheme = () => {
    // Use premium theme as base, but allow switching to other themes
    let baseTheme = allThemes[currentThemeKey] || premiumTheme;

    // If premium theme is selected or default, use our unified premium theme
    if (currentThemeKey === 'premium' || currentThemeKey === 'professional' || !allThemes[currentThemeKey]) {
      baseTheme = premiumTheme;
    }

    // Preserve customEffects from base theme
    const customEffects = baseTheme.customEffects;

    // Apply smart theming if business context is available
    if (Object.keys(businessContext).length > 0) {
      const smartTheme = applySmartTheming(baseTheme, businessContext);
      const theme = createTheme({
        ...smartTheme.theme,
        ...customizations
      });
      // Re-attach customEffects after createTheme, or provide default fallback
      theme.customEffects = customEffects || {
        gradients: {
          primary: createSafeGradient(theme.palette.primary?.main, theme.palette.primary?.dark),
          secondary: createSafeGradient(theme.palette.secondary?.main, theme.palette.secondary?.dark),
          accent: createSafeGradient(theme.palette.secondary?.light, theme.palette.secondary?.main),
          luxury: createSafeGradient(theme.palette.primary?.dark, theme.palette.primary?.main),
          luxuryText: theme.palette.primary?.dark && theme.palette.primary?.main
            ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`
            : (theme.palette.primary?.main || '#1976d2'),
          glass: 'linear-gradient(145deg, #ffffff 0%, rgba(255, 255, 255, 0.8) 100%)',
          nav: createSafeGradient(theme.palette.primary?.main, theme.palette.primary?.dark),
        },
        animations: {
          float: 'float 6s ease-in-out infinite',
          shimmer: 'shimmer 1.5s infinite',
          glow: 'glow 2s ease-in-out infinite alternate',
        },
        glass: {
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(20px)',
        },
        shadows: {
          glow: theme.palette.primary?.main ? `0 0 20px ${theme.palette.primary.main}40` : '0 0 20px rgba(25, 118, 210, 0.25)',
          glowLuxury: theme.palette.primary?.main ? `0 0 40px ${theme.palette.primary.main}30` : '0 0 40px rgba(25, 118, 210, 0.19)',
          premium: theme.palette.primary?.main ? `0 25px 60px ${theme.palette.primary.main}30, 0 0 80px ${theme.palette.primary.main}15` : '0 25px 60px rgba(25, 118, 210, 0.19), 0 0 80px rgba(25, 118, 210, 0.09)',
        },
      };
      return theme;
    }

    const theme = createTheme({
      ...baseTheme,
      ...customizations
    });
    // Re-attach customEffects after createTheme, or provide default fallback
    theme.customEffects = customEffects || {
      gradients: {
        primary: createSafeGradient(theme.palette.primary?.main, theme.palette.primary?.dark),
        secondary: createSafeGradient(theme.palette.secondary?.main, theme.palette.secondary?.dark),
        accent: createSafeGradient(theme.palette.secondary?.light, theme.palette.secondary?.main),
        luxury: createSafeGradient(theme.palette.primary?.dark, theme.palette.primary?.main),
        luxuryText: theme.palette.primary?.dark && theme.palette.primary?.main
          ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`
          : (theme.palette.primary?.main || '#1976d2'),
        glass: 'linear-gradient(145deg, #ffffff 0%, rgba(255, 255, 255, 0.8) 100%)',
        nav: createSafeGradient(theme.palette.primary?.main, theme.palette.primary?.dark),
      },
      animations: {
        float: 'float 6s ease-in-out infinite',
        shimmer: 'shimmer 1.5s infinite',
        glow: 'glow 2s ease-in-out infinite alternate',
      },
      glass: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(20px)',
      },
      shadows: {
        glow: theme.palette.primary?.main ? `0 0 20px ${theme.palette.primary.main}40` : '0 0 20px rgba(25, 118, 210, 0.25)',
        glowLuxury: theme.palette.primary?.main ? `0 0 40px ${theme.palette.primary.main}30` : '0 0 40px rgba(25, 118, 210, 0.19)',
        premium: theme.palette.primary?.main ? `0 25px 60px ${theme.palette.primary.main}30, 0 0 80px ${theme.palette.primary.main}15` : '0 25px 60px rgba(25, 118, 210, 0.19), 0 0 80px rgba(25, 118, 210, 0.09)',
      },
    };
    return theme;
  };

  const theme = generateTheme();

  // Theme context value
  const contextValue = {
    currentTheme: currentThemeKey,
    availableThemes: Object.keys(allThemes),
    businessContext,
    customizations,
    
    // Methods
    setTheme: async (themeKey, context = {}) => {
      setCurrentThemeKey(themeKey);
      await saveThemeToProfile(themeKey);
      if (Object.keys(context).length > 0) {
        setBusinessContext(context);
      }
    },
    
    updateBusinessContext: (context) => {
      setBusinessContext(context);
    },
    
    applyCustomizations: (customizations) => {
      setCustomizations(customizations);
    },
    
    resetToDefault: () => {
      setCurrentThemeKey('default');
      setBusinessContext({});
      setCustomizations({});
    },
    
    // Theme preview for template selector
    getThemePreview: (themeKey) => {
      return allThemes[themeKey] || customThemes.default;
    },
    
    // Get industry-appropriate theme
    getSmartTheme: (businessInfo) => {
      // This would integrate with the smart theming utility
      const recommendations = getSmartThemeRecommendations(businessInfo);
      if (recommendations && recommendations[0]) {
        return recommendations[0].theme;
      }
      return 'default';
    },
    
    // Utility functions for theme management
    validateThemeContrast: (themeKey) => {
      const theme = allThemes[themeKey];
      return validateAndFixTheme(theme);
    },
    
    // Get contrast ratio between two colors
    getContrastRatio: getContrastRatio,
    
    // Force refresh theme with fixes
    refreshTheme: () => {
      const currentThemeConfig = allThemes[currentThemeKey];
      const fixedTheme = validateAndFixTheme(currentThemeConfig);
      return fixedTheme;
    }
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
